import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { FinanceOverview, FinanceSummary } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { formatMoney } from './money';
import { periodLabel, presets as periodPresets } from './period';

const W = 1920;
const H = 1080;

const THEMES = {
  oscuro: { name: 'Oscuro (proyector)', bg: ['#1F3440', '#16262F'], fg: '#FFFFFF', muted: '#B9CDD1', income: '#7FC0C4', expense: '#F2A07B', panel: 'rgba(255,255,255,0.06)' },
  claro: { name: 'Claro', bg: ['#F7F4EC', '#FFFFFF'], fg: '#1F3440', muted: '#5B6B73', income: '#2F6F75', expense: '#B5502A', panel: 'rgba(31,52,64,0.05)' },
} as const;
type ThemeKey = keyof typeof THEMES;

type Row = { id: string; name: string; cents: number };

/** Recorta un texto con «…» para que quepa en el ancho. */
function fit(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t}…`;
}

/** Hasta 8 renglones: si hay más, los menores se juntan en «Otros». */
function top(rows: Row[]): Row[] {
  const sorted = [...rows].sort((a, b) => b.cents - a.cents);
  if (sorted.length <= 8) return sorted;
  const rest = sorted.slice(7);
  return [...sorted.slice(0, 7), { id: 'otros', name: 'Otros', cents: rest.reduce((n, r) => n + r.cents, 0) }];
}

/**
 * Imagen de Finanzas para proyectar en pantallas (1920×1080): el periodo, los ingresos y los gastos que
 * se elijan, en letra grande, con el logo de la iglesia y, pequeño en una esquina, el de KEORA.
 */
export function FinanceImagePage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const overview = useAsync(
    (signal) => (churchId ? api.get<FinanceOverview>(`/finance/churches/${churchId}/accounts`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId],
  );
  const today = overview.status === 'ready' ? overview.data.today : null;
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [custom, setCustom] = useState(false);
  // Por defecto, la semana en curso (lunes a domingo): es lo que se informa en el culto.
  useEffect(() => {
    if (today && !from) {
      const week = periodPresets(today)[0]!;
      setFrom(week.from);
      setTo(week.to);
    }
  }, [today, from]);

  const summary = useAsync(
    (signal) => (churchId && from && to ? api.get<FinanceSummary>(`/finance/churches/${churchId}/summary?from=${from}&to=${to}`, signal) : Promise.resolve(null)),
    [churchId, from, to],
  );
  const church = useAsync(
    (signal) => (churchId ? api.get<{ name: string; logoUrl: string | null }>(`/multichurch/public/join/${churchId}`, signal) : Promise.resolve(null)),
    [churchId],
  );

  const [title, setTitle] = useState('Informe financiero');
  const [theme, setTheme] = useState<ThemeKey>('oscuro');
  const [showIncome, setShowIncome] = useState(true);
  const [showExpense, setShowExpense] = useState(true);
  const [showBalance, setShowBalance] = useState(true);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const canvas = useRef<HTMLCanvasElement>(null);
  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  const [keora, setKeora] = useState<HTMLImageElement | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ ok: boolean; text: string } | null>(null);

  const logoUrl = church.status === 'ready' ? church.data?.logoUrl ?? null : null;
  const churchName = church.status === 'ready' ? church.data?.name ?? '' : '';
  useEffect(() => {
    if (!logoUrl) return setLogo(null);
    const img = new Image();
    img.onload = () => setLogo(img);
    img.onerror = () => setLogo(null);
    img.src = logoUrl;
  }, [logoUrl]);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setKeora(img);
    img.src = '/keora-firma.png';
  }, []);

  const data = summary.status === 'ready' ? summary.data : null;
  const incomeRows: Row[] = data ? data.income.map((r) => ({ id: r.category.id, name: r.category.name, cents: r.totalCents })) : [];
  const expenseRows: Row[] = data ? data.expense.map((r) => ({ id: r.category.id, name: r.category.name, cents: r.totalCents })) : [];
  const shownIncome = incomeRows.filter((r) => !hidden.has(r.id));
  const shownExpense = expenseRows.filter((r) => !hidden.has(r.id));
  const sum = (rows: Row[]) => rows.reduce((n, r) => n + r.cents, 0);
  const money = (c: number) => formatMoney(c, 'MXN');

  const key = JSON.stringify([title, theme, showIncome, showExpense, showBalance, [...hidden], from, to, data?.incomeCents, data?.expenseCents, incomeRows.length, expenseRows.length, churchName]);
  useEffect(() => {
    const c = canvas.current;
    const ctx = c?.getContext?.('2d');
    if (!c || !ctx || !data || !from || !to) return;
    const t = THEMES[theme];
    c.width = W;
    c.height = H;
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, t.bg[0]);
    g.addColorStop(1, t.bg[1]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    const pad = 90;

    // Encabezado: logo y nombre de la iglesia a la izquierda; título y periodo.
    let x = pad;
    if (logo) {
      const s = 130;
      const r = Math.min(s / logo.width, s / logo.height);
      ctx.drawImage(logo, x, 60 + (s - logo.height * r) / 2, logo.width * r, logo.height * r);
      x += s + 36;
    }
    ctx.textAlign = 'left';
    ctx.fillStyle = t.muted;
    ctx.font = '600 40px system-ui, sans-serif';
    ctx.fillText(fit(ctx, churchName, W - x - pad), x, 100);
    ctx.fillStyle = t.fg;
    ctx.font = 'bold 64px Georgia, "Times New Roman", serif';
    ctx.fillText(fit(ctx, title || 'Informe financiero', W - x - pad), x, 170);
    ctx.fillStyle = t.muted;
    ctx.font = '40px system-ui, sans-serif';
    ctx.fillText(periodLabel(from, to), x, 225);

    // Columnas: ingresos y gastos.
    const columns: { label: string; color: string; rows: Row[] }[] = [];
    if (showIncome) columns.push({ label: 'INGRESOS', color: t.income, rows: top(shownIncome) });
    if (showExpense) columns.push({ label: 'GASTOS', color: t.expense, rows: top(shownExpense) });
    const bodyTop = 280;
    const balanceH = showBalance ? 150 : 0;
    const bodyBottom = H - 70 - balanceH;
    const gap = 50;
    const colW = columns.length ? (W - pad * 2 - gap * (columns.length - 1)) / columns.length : 0;
    columns.forEach((col, i) => {
      const cx = pad + i * (colW + gap);
      ctx.fillStyle = t.panel;
      ctx.beginPath();
      ctx.roundRect(cx, bodyTop, colW, bodyBottom - bodyTop, 28);
      ctx.fill();
      ctx.fillStyle = col.color;
      ctx.fillRect(cx, bodyTop, 14, bodyBottom - bodyTop);
      const inner = cx + 60;
      const innerW = colW - 100;
      ctx.textAlign = 'left';
      ctx.font = 'bold 40px system-ui, sans-serif';
      ctx.fillText(col.label, inner, bodyTop + 70);
      ctx.fillStyle = t.fg;
      const totalSize = columns.length === 1 ? 120 : 96;
      ctx.font = `bold ${totalSize}px system-ui, sans-serif`;
      ctx.fillText(fit(ctx, money(sum(col.rows)), innerW), inner, bodyTop + 80 + totalSize);
      // Renglones por concepto, tan grandes como quepan.
      const listTop = bodyTop + 120 + totalSize;
      const space = bodyBottom - 40 - listTop;
      if (col.rows.length === 0) {
        ctx.fillStyle = t.muted;
        ctx.font = '40px system-ui, sans-serif';
        ctx.fillText('Sin movimientos en el periodo', inner, listTop + 50);
        return;
      }
      const size = Math.max(30, Math.min(56, Math.floor(space / col.rows.length / 1.55)));
      const lh = Math.min(size * 1.55, space / col.rows.length);
      col.rows.forEach((r, j) => {
        const y = listTop + size + j * lh;
        ctx.font = `bold ${size}px system-ui, sans-serif`;
        ctx.textAlign = 'right';
        ctx.fillStyle = t.fg;
        const amount = money(r.cents);
        ctx.fillText(amount, inner + innerW, y);
        const aw = ctx.measureText(amount).width;
        ctx.textAlign = 'left';
        ctx.font = `${size}px system-ui, sans-serif`;
        ctx.fillStyle = t.muted;
        ctx.fillText(fit(ctx, r.name, innerW - aw - 30), inner, y);
      });
    });
    if (columns.length === 0 && !showBalance) {
      ctx.textAlign = 'center';
      ctx.fillStyle = t.muted;
      ctx.font = '48px system-ui, sans-serif';
      ctx.fillText('Elige qué mostrar', W / 2, H / 2);
    }

    // Balance del periodo (todos los ingresos menos todas los gastos).
    if (showBalance) {
      const balance = data.incomeCents - data.expenseCents;
      const by = columns.length ? H - 70 - balanceH + 30 : H / 2 - 60;
      ctx.fillStyle = t.muted;
      const label = 'Balance del periodo  ';
      ctx.font = 'bold 84px system-ui, sans-serif';
      const amount = money(balance);
      const aw = ctx.measureText(amount).width;
      ctx.font = '600 44px system-ui, sans-serif';
      const lw = ctx.measureText(label).width;
      const start = W / 2 - (lw + aw) / 2;
      ctx.textAlign = 'left';
      ctx.fillText(label, start, by + 80);
      ctx.fillStyle = balance < 0 ? t.expense : t.fg;
      ctx.font = 'bold 84px system-ui, sans-serif';
      ctx.fillText(amount, start + lw, by + 84);
    }

    // KEORA pequeño en la esquina inferior derecha (sobre una pastilla clara para que se lea en fondo oscuro).
    if (keora) {
      const kw = 150;
      const kh = (keora.height / keora.width) * kw;
      const kx = W - kw - 40;
      const ky = H - kh - 28;
      if (theme === 'oscuro') {
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.beginPath();
        ctx.roundRect(kx - 12, ky - 6, kw + 24, kh + 12, 14);
        ctx.fill();
      }
      ctx.drawImage(keora, kx, ky, kw, kh);
    }
  }, [key, logo, keora]);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  const fileName = `finanzas-${from ?? ''}-a-${to ?? ''}.png`;
  function download() {
    canvas.current?.toBlob((b) => {
      if (!b) return;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    }, 'image/png');
  }
  async function sendToMultimedia() {
    const dataUrl = canvas.current?.toDataURL('image/png');
    if (!churchId || !dataUrl || !from || !to) return;
    setSending(true);
    setSent(null);
    try {
      const name = `Finanzas - ${title || 'Informe financiero'} - ${from} a ${to}`;
      const r = await api.post<{ id: string; notified: number }>(`/finance/churches/${churchId}/projection-images`, { name, pngBase64: dataUrl.split(',')[1] });
      setSent({ ok: true, text: r.notified ? `Enviada a Multimedia: se avisó a ${r.notified} ${r.notified === 1 ? 'persona' : 'personas'}. La encuentran en Multimedia para descargarla.` : 'Enviada a Multimedia. Aún no hay integrantes en el ministerio de Multimedia para avisarles.' });
    } catch (e) {
      setSent({ ok: false, text: errorMessage(e) });
    } finally {
      setSending(false);
    }
  }
  function fullscreen() {
    void canvas.current?.requestFullscreen?.().catch(() => undefined);
  }
  const toggle = (id: string) =>
    setHidden((h) => {
      const n = new Set(h);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const presets = today ? [...periodPresets(today), { key: 'anio', label: 'Este año', from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` }] : [];
  const preset = custom ? null : presets.find((p) => p.from === from && p.to === to) ?? null;
  const checks = (rows: Row[]) => (
    <div className="chips">
      {rows.map((r) => (
        <label key={r.id} className={`chip${hidden.has(r.id) ? '' : ' chip--active'}`}>
          <input type="checkbox" checked={!hidden.has(r.id)} onChange={() => toggle(r.id)} /> {r.name}
        </label>
      ))}
    </div>
  );

  return (
    <div className="page">
      <p className="small">
        <Link to="/app/finanzas">← Finanzas</Link>
      </p>
      <header className="page__header">
        <h1>Imagen para proyectar</h1>
        <p className="muted">Elige el periodo y qué mostrar. La imagen sale en tamaño de pantalla (1920×1080) con el logo de la iglesia.</p>
      </header>
      {overview.status === 'error' ? <ErrorState error={overview.error} onRetry={overview.reload} /> : null}
      {summary.status === 'error' ? <ErrorState error={summary.error} onRetry={summary.reload} /> : null}
      <div className="stack-grid">
        <Card title="Qué mostrar">
          <div className="chips" role="group" aria-label="Periodo rápido">
            {presets.map((p) => (
              <button key={p.key} type="button" className={`chip${preset?.key === p.key ? ' chip--active' : ''}`} aria-pressed={preset?.key === p.key} onClick={() => (setCustom(false), setFrom(p.from), setTo(p.to))}>
                {p.label}
              </button>
            ))}
            <button type="button" className={`chip${preset ? '' : ' chip--active'}`} aria-pressed={!preset} onClick={() => setCustom(true)}>
              Fechas a elegir
            </button>
          </div>
          {preset ? null : (
            <div className="form__row">
              <Field label="Desde" name="from" type="date" value={from ?? ''} max={to ?? undefined} onChange={(e) => e.target.value && setFrom(e.target.value)} />
              <Field label="Hasta" name="to" type="date" value={to ?? ''} min={from ?? undefined} onChange={(e) => e.target.value && setTo(e.target.value)} />
            </div>
          )}
          {from && to ? <p className="small muted">La imagen suma solo lo de: {periodLabel(from, to)}.</p> : null}
          <Field label="Título" name="title" value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} />
          <label className="check" style={{ display: 'flex', margin: '0.5rem 0' }}>
            <input type="checkbox" checked={showIncome} onChange={(e) => setShowIncome(e.target.checked)} /> Ingresos
          </label>
          {showIncome && incomeRows.length ? checks(incomeRows) : null}
          <label className="check" style={{ display: 'flex', margin: '0.5rem 0' }}>
            <input type="checkbox" checked={showExpense} onChange={(e) => setShowExpense(e.target.checked)} /> Gastos
          </label>
          {showExpense && expenseRows.length ? checks(expenseRows) : null}
          <label className="check" style={{ display: 'flex', margin: '0.5rem 0' }}>
            <input type="checkbox" checked={showBalance} onChange={(e) => setShowBalance(e.target.checked)} /> Balance del periodo (todo lo que entró menos todo lo que salió)
          </label>
          <div className="chips" role="radiogroup" aria-label="Estilo">
            {(Object.keys(THEMES) as ThemeKey[]).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={theme === k} className={`chip${theme === k ? ' chip--active' : ''}`} onClick={() => setTheme(k)}>
                {THEMES[k].name}
              </button>
            ))}
          </div>
        </Card>
        <Card title="Vista previa">
          {summary.status === 'loading' || !data ? <Loading /> : null}
          <canvas ref={canvas} className="finance-image" aria-label="Imagen de finanzas" style={{ width: '100%', height: 'auto', display: data ? 'block' : 'none', borderRadius: 8 }} />
          <div className="actions">
            <Button onClick={download} disabled={!data}>
              Descargar imagen
            </Button>
            <Button variant="secondary" onClick={fullscreen} disabled={!data}>
              Pantalla completa
            </Button>
            {me.can('finance.manage') ? (
              <Button variant="secondary" onClick={() => void sendToMultimedia()} disabled={!data} busy={sending}>
                Enviar a Multimedia
              </Button>
            ) : null}
          </div>
          {sent ? <Alert tone={sent.ok ? 'success' : 'error'}>{sent.text}</Alert> : null}
        </Card>
      </div>
    </div>
  );
}
